import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import ContactFooter from "./ContactFooter";
import getLocalizedValue from "../utils/getLocalizedValue";
import "./products/ProductOverview.css";

const publicUrl = process.env.PUBLIC_URL || "";

const coldplates = [
  {
    id: "coldplate-package",
    title: "Coldplate for Package",
    titleKo: "패키지용 콜드플레이트",
    imageSrc: require("../assets/product/cpu-immc-angle.png"),
    hoverSrc: require("../assets/product/cpu-immc-top-view.png"),
  },
  {
    id: "coldplate-module",
    title: "Coldplate for Module",
    titleKo: "모듈용 콜드플레이트",
    imageSrc: `${publicUrl}/svg/product/coldplate2-1.svg`,
    hoverSrc: `${publicUrl}/svg/product/coldplate2-2.svg`,
  },
  {
    id: "coldplate-multi-package",
    title: "Coldplate for Multi Package",
    titleKo: "멀티 패키지용 콜드플레이트",
    imageSrc: `${publicUrl}/svg/product/coldplate3-1.svg`,
    hoverSrc: `${publicUrl}/svg/product/coldplate3-2.svg`,
  },
];

const thermalTestVehicles = [
  {
    id: "ttv-single-chip",
    title: "TTV for Single Chip Package",
    titleKo: "싱글 칩 패키지용 TTV",
    imageSrc: `${publicUrl}/svg/product/ttv2-1.svg`,
    hoverSrc: `${publicUrl}/svg/product/ttv2-2.svg`,
  },
  {
    id: "ttv-multi-chip",
    title: "TTV for Multi Chip Package",
    titleKo: "멀티 칩 패키지용 TTV",
    imageSrc: `${publicUrl}/svg/product/ttv1-1.svg`,
    hoverSrc: `${publicUrl}/svg/product/ttv1-2.svg`,
  },
  {
    id: "ttv-server",
    title: "TTV for Servers",
    titleKo: "서버용 TTV",
    imageSrc: `${publicUrl}/svg/product/ttv3-1.svg`,
    hoverSrc: `${publicUrl}/svg/product/ttv3-2.svg`,
  },
];

function ArrowIcon({ direction }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="product-carousel__arrow-icon"
    >
      <path
        d={direction === "previous" ? "M14.5 5 7.5 12l7 7" : "m9.5 5 7 7-7 7"}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function InlineArrow() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 16" className="product-inline-arrow">
      <path
        d="M1 8h23m-6-6 6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function relativePosition(index, activeIndex, length) {
  let position = (index - activeIndex + length) % length;
  if (position > length / 2) position -= length;
  return position;
}

function ProductCarousel({
  id,
  eyebrow,
  eyebrowKo,
  title,
  titleKo,
  description,
  descriptionKo,
  items,
  tone = "ice",
}) {
  const [activeIndex, setActiveIndex] = useState(1);
  const reduceMotion = useReducedMotion();

  const selectRelative = useCallback(
    (direction) => {
      setActiveIndex((current) => (current + direction + items.length) % items.length);
    },
    [items.length]
  );

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      selectRelative(-1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      selectRelative(1);
    }
  };

  const handleDragEnd = (_, info) => {
    const dragDistance = info.offset.x;
    const dragSpeed = info.velocity.x;

    if (dragDistance < -55 || dragSpeed < -450) selectRelative(1);
    if (dragDistance > 55 || dragSpeed > 450) selectRelative(-1);
  };

  return (
    <section
      className={`product-family product-family--${tone}`}
      aria-labelledby={`${id}-title`}
    >
      <div className="product-family__header">
        <div>
          <p className="product-family__eyebrow">
            {getLocalizedValue(eyebrow, eyebrowKo)}
          </p>
          <h2 id={`${id}-title`}>{getLocalizedValue(title, titleKo)}</h2>
          <p className="product-family__description">
            {getLocalizedValue(description, descriptionKo)}
          </p>
        </div>
        <p className="product-family__drag-hint" aria-hidden="true">
          {getLocalizedValue("Drag to explore", "드래그하여 살펴보기")}
        </p>
      </div>

      <div
        className="product-carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label={getLocalizedValue(eyebrow, eyebrowKo)}
        onKeyDown={handleKeyDown}
      >
        <motion.div
          className="product-carousel__viewport"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={reduceMotion ? 0 : 0.16}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          style={{ touchAction: "pan-y" }}
        >
          <div className="product-carousel__centerline">
            {items.map((item, index) => {
              const position = relativePosition(index, activeIndex, items.length);
              const active = position === 0;
              const localizedTitle = getLocalizedValue(item.title, item.titleKo);

              return (
                <motion.button
                  type="button"
                  key={item.id}
                  className={`product-card${active ? " product-card--active" : ""}${
                    item.id === "coldplate-package"
                      ? " product-card--cpu-immc"
                      : ""
                  }`}
                  animate={{
                    x: `${position * 92}%`,
                    scale: active ? 1 : 0.78,
                    opacity: active ? 1 : 0.48,
                  }}
                  transition={
                    reduceMotion
                      ? { duration: 0 }
                      : { type: "spring", stiffness: 250, damping: 28, mass: 0.8 }
                  }
                  style={{ zIndex: active ? 3 : 1 }}
                  onClick={() => setActiveIndex(index)}
                  aria-label={
                    active
                      ? localizedTitle
                      : getLocalizedValue(
                          `Center ${localizedTitle}`,
                          `${localizedTitle} 중앙에서 보기`
                        )
                  }
                  aria-pressed={active}
                >
                  <span className="product-card__image-wrap">
                    <img
                      className="product-card__image product-card__image--primary"
                      src={item.imageSrc}
                      alt=""
                      draggable="false"
                    />
                    <img
                      className="product-card__image product-card__image--detail"
                      src={item.hoverSrc}
                      alt=""
                      draggable="false"
                    />
                  </span>
                  <span className="product-card__title">{localizedTitle}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        <button
          type="button"
          className="product-carousel__arrow product-carousel__arrow--previous"
          onClick={() => selectRelative(-1)}
          aria-label={getLocalizedValue("Previous product", "이전 제품")}
        >
          <ArrowIcon direction="previous" />
        </button>
        <button
          type="button"
          className="product-carousel__arrow product-carousel__arrow--next"
          onClick={() => selectRelative(1)}
          aria-label={getLocalizedValue("Next product", "다음 제품")}
        >
          <ArrowIcon direction="next" />
        </button>

        <div
          className="product-carousel__dots"
          role="group"
          aria-label={getLocalizedValue("Choose a product", "제품 선택")}
        >
          {items.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={`product-carousel__dot${index === activeIndex ? " product-carousel__dot--active" : ""}`}
              onClick={() => setActiveIndex(index)}
              aria-label={getLocalizedValue(item.title, item.titleKo)}
              aria-current={index === activeIndex ? "true" : undefined}
            />
          ))}
        </div>

        <p className="product-carousel__status" aria-live="polite" aria-atomic="true">
          {getLocalizedValue(items[activeIndex].title, items[activeIndex].titleKo)}
        </p>
      </div>
    </section>
  );
}

function ProductPage() {
  return (
    <div
      className="product-overview-scroll w-full flex-grow overflow-y-scroll overflow-x-hidden"
      style={{ scrollSnapType: "y proximity" }}
    >
      <main className="product-overview">
        <ProductCarousel
          id="immc-coldplates"
          eyebrow="IMMC Coldplates"
          eyebrowKo="IMMC 콜드플레이트"
          title="Cooling at the source."
          titleKo="열이 시작되는 곳에서 냉각합니다."
          description="Efficient heat transfer, uniform temperature, and low pumping power."
          descriptionKo="효율적인 열 전달과 균일한 온도, 낮은 펌핑 동력을 제공합니다."
          items={coldplates}
        />

        <section
          className="liquidiq-feature-section"
          aria-labelledby="liquidiq-feature-title"
        >
          <article className="liquidiq-feature">
            <div className="liquidiq-feature__copy">
              <p className="liquidiq-feature__release">
                {getLocalizedValue(
                  "Releasing February 28th 2027",
                  "2027년 2월 28일 출시 예정"
                )}
              </p>
              <h2 id="liquidiq-feature-title">LiquidIQ</h2>
              <p>
                {getLocalizedValue(
                  "Energy Efficient Liquid Cooling Compute Platform",
                  "에너지 효율을 높인 액체 냉각 컴퓨팅 플랫폼"
                )}
              </p>
              <Link className="liquidiq-feature__link" to="/product/liquidiq">
                {getLocalizedValue("Explore LiquidIQ", "LiquidIQ 살펴보기")}
                <InlineArrow />
              </Link>
            </div>
            <div className="liquidiq-feature__visual">
              <img
                src={`${publicUrl}/images/liquidiq/white-back-hq.png`}
                alt={getLocalizedValue(
                  "LiquidIQ liquid cooled workstation",
                  "LiquidIQ 액체 냉각 워크스테이션"
                )}
              />
            </div>
          </article>
        </section>

        <ProductCarousel
          id="thermal-test-vehicles"
          eyebrow="Thermal Test Vehicles"
          eyebrowKo="열 테스트 비히클"
          title="Test the thermal path."
          titleKo="열의 흐름을 검증합니다."
          description="Measure temperature, flow, pressure, and cooling response before production."
          descriptionKo="생산 전에 온도와 유량, 압력, 냉각 반응을 확인합니다."
          items={thermalTestVehicles}
          tone="blue"
        />

        <section className="immc-technology" aria-labelledby="immc-technology-title">
          <div className="immc-technology__banner">
            <div className="immc-technology__icon" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <div className="immc-technology__copy">
              <h2 id="immc-technology-title">
                {getLocalizedValue(
                  "Built on IMMC technology.",
                  "IMMC 기술을 기반으로 합니다."
                )}
              </h2>
              <p>
                {getLocalizedValue(
                  "Integrated Manifold MicroChannel technology supports performance, reliability, and scale.",
                  "Integrated Manifold MicroChannel 기술로 성능과 신뢰성, 확장성을 높입니다."
                )}
              </p>
            </div>
            <Link className="immc-technology__link" to="/technology/immc">
              {getLocalizedValue("Explore the technology", "기술 살펴보기")}
              <InlineArrow />
            </Link>
          </div>
        </section>
      </main>
      <ContactFooter />
    </div>
  );
}

export default ProductPage;
